import { BookingDetails } from '../domain/BookingDetails'
import {
    BookingPaymentTerminology,
    bookingPaymentTerminologyForVenueId,
    usesGenericPaymentTerminology,
} from '../domain/BookingPaymentTerminology'
import { BookingStatusType } from '../domain/BookingStatus'

export class BookingDetailsViewModel {

    public showCancelButton: boolean
    public showCancellationStatus = false
    public showBookAgainButton = false
    public showBookingConfirmedStatus = false
    public showBookingRejectedStatus = false
    public showBookingRequestedStatus = false
    public showPendingDepositStatus = false
    public showPendingCardDetailsStatus = false
    public showCannotModifyBookingText = false
    public showModifyBookingButton = false
    public showGetDirectionsButton = false
    public showViewBookingButton = false
    public showReminderConfirmation = false
    public showPayDepositButton = false
    public showSaveCardDetailsButton = false
    public showDepositUnpaidStatus = false
    public showDepositPaidStatus = false
    public showDepositRefundedStatus = false
    public showDepositPaidSoFar = false
    public showFillMenuChoicesButton = false
    public disableFillMenuChoicesButton = false
    public bookingTableLabel = 'Table'
    public readonly paymentTerminology: BookingPaymentTerminology
    public readonly additionalBookings: BookingDetailsViewModel[]
    public readonly hasSharedStatus: boolean
    public readonly hasSharedDate: boolean

    private readonly laneReasonIds = new Set<string>([
        '15b0d1e2-1842-4670-904f-f7f962658155',
        '4083d42b-672e-4f43-bbd0-0e080b6301f1',
        '5af21ccb-0866-484d-bbb8-e0d7bc7ca556',
        '599f8eef-38bf-4635-a065-169e13c74fe5',
        '77f9eeec-9db3-4d2c-91f4-d3088009a747',
    ])
    private readonly tourVenueIds = new Set<string>([
        'b8b32c4f-7bbb-42c3-a169-fd773946c021', // Ben Nevis Distillery
    ])

    constructor(
        public details: BookingDetails,
        public isShownInWidget: boolean,
        public justConfirmedReminder: boolean
    ) {
        this.additionalBookings = isShownInWidget
            ? details.additionalBookings.map(booking => new BookingDetailsViewModel(booking, true, false))
            : []
        this.hasSharedStatus = [BookingStatusType.Booked, BookingStatusType.Requested,
            BookingStatusType.Cancelled, BookingStatusType.Rejected].includes(details.statusType)
            && this.additionalBookings.every(booking => booking.details.statusType === details.statusType)
        this.hasSharedDate = this.additionalBookings.every(booking =>
            booking.details.dateTime.toDateString() === details.dateTime.toDateString())
        this.paymentTerminology = bookingPaymentTerminologyForVenueId(details.venueId, !details.sale && details.order?.isPaid)
        const customerCanManageBookingOnline = this.details.modifyBookingPath !== null
        this.showCannotModifyBookingText = !customerCanManageBookingOnline
        this.showModifyBookingButton = !isShownInWidget && customerCanManageBookingOnline
        this.showCancelButton = !isShownInWidget &&
            customerCanManageBookingOnline &&
            (details.statusType === BookingStatusType.Booked ||
            details.statusType === BookingStatusType.PendingPayment ||
            details.statusType === BookingStatusType.Requested)
        this.showCancellationStatus = details.statusType === BookingStatusType.Cancelled
        this.showBookAgainButton = isShownInWidget &&
            (details.statusType === BookingStatusType.Booked ||
                details.statusType === BookingStatusType.Requested)
        this.showBookingConfirmedStatus = details.statusType === BookingStatusType.Booked &&
            !justConfirmedReminder
        this.showBookingRejectedStatus = details.statusType === BookingStatusType.Rejected
        this.showBookingRequestedStatus = details.statusType === BookingStatusType.Requested &&
            !justConfirmedReminder
        this.showPendingDepositStatus = details.statusType === BookingStatusType.PendingPayment &&
            (details.depositCharge !== null || details.hasUnpaidOrder)
        this.showPendingCardDetailsStatus = details.statusType === BookingStatusType.PendingPayment &&
            details.cancellationCharge !== null && !details.hasUnpaidOrder
        this.showGetDirectionsButton = details.statusType === BookingStatusType.Booked ||
            details.statusType === BookingStatusType.Requested
        this.showViewBookingButton = isShownInWidget
        this.showPayDepositButton = (details.statusType === BookingStatusType.PendingPayment &&
            (details.depositCharge !== null || details.hasUnpaidOrder)) || (details.hasUnpaidOrder &&
            (details.statusType === BookingStatusType.Booked || details.statusType === BookingStatusType.Requested))
        this.showSaveCardDetailsButton = details.statusType === BookingStatusType.PendingPayment &&
            details.cancellationCharge !== null && !details.hasUnpaidOrder
        const hasOutstandingDeposit = details.depositAmountOutstanding !== null
            ? details.depositAmountOutstanding > 0
            : details.depositCharge !== null &&
                details.depositDatePaid === null &&
                details.depositDateRefunded === null
        const hasPaidDeposit = details.depositAmountPaid !== null
            ? details.depositAmountPaid > 0
            : details.depositDatePaid !== null
        this.showDepositRefundedStatus = details.depositDateRefunded !== null
        this.showDepositUnpaidStatus = !this.showDepositRefundedStatus && hasOutstandingDeposit
        this.showDepositPaidStatus = !this.showDepositRefundedStatus &&
            !this.showDepositUnpaidStatus &&
            hasPaidDeposit
        this.showDepositPaidSoFar = this.showDepositUnpaidStatus &&
            details.depositAmountPaid !== null &&
            details.depositAmountPaid > 0
        this.showReminderConfirmation = justConfirmedReminder
        this.showFillMenuChoicesButton = details.docket !== null
        this.disableFillMenuChoicesButton =
            details.statusType === BookingStatusType.Cancelled ||
            details.statusType === BookingStatusType.Rejected ||
            details.statusType === BookingStatusType.NoShow ||
            details.statusType === BookingStatusType.Finished
        this.bookingTableLabel = this.getBookingTableLabel(details)
    }

    private getBookingTableLabel(details: BookingDetails): string {
        if (details.reasonId && this.laneReasonIds.has(details.reasonId)) {
            return 'Lane'
        }
        if (this.tourVenueIds.has(details.venueId) || usesGenericPaymentTerminology(details.venueId, details.order?.isPaid)) {
            return 'Booking'
        }
        return 'Table'
    }
}
